import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, InputNumber, Select } from 'antd';
import { connect } from 'react-redux';
import {
  createAction,
  updateAction
} from '../../../redux/actions/trm/actionCatalog';
import { fetchAMLSAlertLevel } from '../../../redux/actions/common';
import useAlertLevelConfigs from '../../common/hooks/useAlertLevelConfig';

const NAME_PATTERN = /^[A-Z0-9 _-]{1,64}$/;

/**
 * FIN-13292 — find an existing action already holding this weight.
 *
 * Compared numerically, not by string or reference: the stored weight arrives as a
 * DECIMAL(5,2) and can reach the FE as `100`, `"100"` or `"100.00"` depending on the payload,
 * so "100" and "100.00" must count as the SAME weight. `Number()` normalises all three.
 *
 * `excludingName` is the action currently being edited — re-saving it without changing its
 * weight must not be reported as a clash with itself.
 */
export const findWeightClash = (actions, weight, excludingName) => {
  if (weight === null || weight === undefined || weight === '') return null;
  const target = Number(weight);
  if (Number.isNaN(target)) return null;
  return (
    (Array.isArray(actions) ? actions : []).find(
      (a) =>
        a &&
        Number(a.weight) === target &&
        (!excludingName || a.displayName !== excludingName)
    ) || null
  );
};

function ActionFormModal({
  visible,
  editing,
  onClose,
  alertLevels,
  existingActions,
  fetchAMLSAlertLevel: dispatchFetchAlertLevels,
  createAction: dispatchCreate,
  updateAction: dispatchUpdate
}) {
  const [form] = Form.useForm();
  const { formatAlertLevelOptions } = useAlertLevelConfigs(alertLevels);

  // FIN-12133: severity options come from alert_level_details (the shared
  // alertLevelClassification list) — show displayName, store the code.
  const severityOptions = useMemo(
    () => formatAlertLevelOptions(),
    [formatAlertLevelOptions]
  );

  useEffect(() => {
    if (!visible) return;
    if (
      typeof dispatchFetchAlertLevels === 'function' &&
      (!Array.isArray(alertLevels) || alertLevels.length === 0)
    ) {
      dispatchFetchAlertLevels();
    }
  }, [visible, alertLevels, dispatchFetchAlertLevels]);

  useEffect(() => {
    if (!visible) return;
    if (editing) {
      form.setFieldsValue({
        displayName:
          typeof editing.displayName === 'string'
            ? editing.displayName.toUpperCase()
            : editing.displayName,
        weight: Number(editing.weight),
        description: editing.description || '',
        // stored underlying code; the Select resolves it to the display name
        actionSeverity: editing.actionSeverity || undefined
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ weight: 0 });
    }
  }, [visible, editing, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        displayName: values.displayName,
        weight: values.weight,
        description: values.description || null,
        // pass the underlying severity code to the backend (null clears it)
        actionSeverity: values.actionSeverity || null,
        sortOrder:
          editing && typeof editing.sortOrder === 'number'
            ? editing.sortOrder
            : 0
      };
      if (editing) {
        dispatchUpdate(editing.displayName, payload);
      } else {
        dispatchCreate(payload);
      }
      onClose();
    } catch (_e) {
      // antd validateFields rejects on invalid; let the form display errors
    }
  };

  return (
    <Modal
      visible={visible}
      onOk={handleOk}
      onCancel={onClose}
      title={editing ? 'Edit Action' : 'Add Action'}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="displayName"
          label="Display name"
          normalize={(v) => (typeof v === 'string' ? v.toUpperCase() : v)}
          rules={[
            { required: true, message: 'Display name is required' },
            {
              pattern: NAME_PATTERN,
              message:
                'Use uppercase A–Z, digits, space, underscore, hyphen (max 64)'
            }
          ]}
        >
          <Input disabled={!!editing} placeholder="E.g. STOP" maxLength={64} />
        </Form.Item>
        <Form.Item
          name="weight"
          label="Weight (0–100, up to 2 decimals)"
          rules={[
            {
              required: true,
              type: 'number',
              min: 0,
              max: 100,
              message: 'Weight must be between 0 and 100'
            },
            // FIN-13292 — weight must be unique across the catalogue. The backend rejects a
            // clash with a 409, but without this the analyst only finds out after submitting.
            // The action being edited is excluded so re-saving it unchanged is not a clash
            // with itself.
            //
            // ...and the check is SKIPPED ENTIRELY when the weight has not moved, mirroring
            // ScalikeFraudActionsService.update. Excluding self is not enough on a catalogue
            // that ALREADY holds two actions at one weight: each finds the other, so both rows
            // become uneditable — and here that is worse than on the BE, because the form
            // blocks before any request is sent and the name input is disabled while editing,
            // leaving no way out but moving the weight.
            //
            // Not hypothetical on upgraded clients: v6.4.0_action_catalog.sql adds
            // `weight DEFAULT 0` and backfills only ALLOW / SOFT STOP / STOP / REJECTED, so
            // every custom action and the CM 'Hard stop' seed start life sharing weight 0.
            {
              validator: (_rule, value) => {
                const unchanged =
                  editing != null &&
                  editing.weight != null &&
                  Number(value) === Number(editing.weight);
                if (unchanged) return Promise.resolve();
                const clash = findWeightClash(
                  existingActions,
                  value,
                  editing?.displayName
                );
                if (!clash) return Promise.resolve();
                const used = clash.displayName;
                return Promise.reject(
                  new Error(`Weight ${value} is already used by ${used}`)
                );
              }
            }
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            precision={2}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item name="actionSeverity" label="Severity">
          <Select
            allowClear
            placeholder="Select severity"
            options={severityOptions}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[{ max: 500, message: 'Max 500 characters' }]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}

ActionFormModal.propTypes = {
  visible: PropTypes.bool,
  editing: PropTypes.shape({
    displayName: PropTypes.string,
    weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    sortOrder: PropTypes.number,
    actionSeverity: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  alertLevels: PropTypes.array,
  existingActions: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string,
      weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  fetchAMLSAlertLevel: PropTypes.func,
  createAction: PropTypes.func.isRequired,
  updateAction: PropTypes.func.isRequired
};

ActionFormModal.defaultProps = {
  visible: false,
  editing: null,
  alertLevels: [],
  existingActions: [],
  fetchAMLSAlertLevel: undefined
};

const mapStateToProps = (state) => ({
  alertLevels: state.amlsCommon?.alertLevelConfigs?.data || [],
  // FIN-13292 — the catalogue the parent screen already holds; read here rather than threaded
  // through as a prop so the modal validates against the same list the table renders.
  existingActions: state.actionCatalog?.rows || []
});

export default connect(mapStateToProps, {
  createAction,
  updateAction,
  fetchAMLSAlertLevel
})(ActionFormModal);
